/**
 * 건축 자재 카탈로그 — 벽·바닥·창·문·지붕.
 *
 * 가구와 같은 조각 시스템을 쓰지만 쓰이는 방식이 다르다. 가구는 씬에 그룹으로 얹지만
 * 건축은 **StaticBatcher 로 구워 넣는다** (world.ts 의 bakePiece). 벽이 203 세그먼트,
 * 창이 37 장이라 그룹으로 얹으면 draw call 이 수백 개가 된다.
 * 자세한 이유와 절차는 docs/ARCHITECTURE_CATALOG.md 에 있다.
 *
 * 좌표: 원점은 **개구부 바닥 중앙**. y=0 이 창턱(sill)이나 문턱 높이가 되고,
 * XZ 원점은 개구부 중심이다. 두께 방향은 Z 이고 **-Z 가 실외**다.
 * 벽에 난 구멍에 그대로 끼우면 맞는다 — world.ts 가 `[win.x, WIN_Y.sill, win.z]` 를 넘긴다.
 *
 * 「사흘」은 1998 년에 닫은 분교를 청년들이 고쳐 쓰는 마을이다. 골조는 90 년대,
 * 사는 사람은 지금 사람이다. 창틀은 그때 달린 알루미늄 미서기창이어야 한다 —
 * 나무 창틀로 만들면 시대가 한 세대 밀린다.
 */

import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import type { MeshWriter } from './kernel';
import type { Piece } from './pieces';

/**
 * 상자 하나를 지정 위치에 놓는다 — 건축은 직선 부재가 대부분이라 이게 기본 단위다.
 *
 * **베벨을 쓰지 않는다.** `bevelBox` + `localUV` 조합이 표면을 흰 점으로 얼룩지게 만든다:
 * localUV 는 정점 normal 로 삼평면 투영면을 고르는데(kernel.ts:233) 베벨 링의 normal 은
 * 45도라 인접 정점이 서로 다른 투영면을 골라 **삼각형 안에서 UV 가 찢어진다.**
 * 그러면 darkwood 의 고주파 노멀(fbm 128)이 랜덤 샘플링돼 스페큘러가 점으로 튄다.
 *
 * 문틀을 베벨 상자로 짰다가 이 현상을 만났고, z-파이팅과 그림자 acne 를 먼저 의심해
 * 두 번 헛짚었다. 축정렬 Box 는 면마다 normal 이 하나라 투영면이 갈리지 않는다.
 * 2mm 베벨은 건축 크기에서 보이지도 않고 삼각형도 Box 가 훨씬 적다.
 */
function bar(
  w: MeshWriter,
  slot: Parameters<MeshWriter['add']>[1],
  sx: number,
  sy: number,
  sz: number,
  x: number,
  y: number,
  z: number,
  _bevel = 0,
  uv = 0.9,
) {
  const g = new THREE.BoxGeometry(sx, sy, sz);
  g.translate(x, y, z);
  w.add(g, slot, uv);
}


/** 정점 색을 통째로 입힌다 — 슬롯 재질색에 곱해지므로 `paper`(#ded6c2) 위에 올릴 색은 그만큼 나눠 준다 */
function tinted(geo: THREE.BufferGeometry, hex: number, over: [number, number, number] = [0.87, 0.84, 0.76]) {
  const g = geo.index ? geo.toNonIndexed() : geo;
  const r = (((hex >> 16) & 255) / 255) / over[0], gch = (((hex >> 8) & 255) / 255) / over[1], b = ((hex & 255) / 255) / over[2];
  const n = g.attributes.position.count; const c = new Float32Array(n * 3);
  for (let i = 0; i < n; i++) { c[i * 3] = r; c[i * 3 + 1] = gch; c[i * 3 + 2] = b; }
  g.setAttribute('color', new THREE.BufferAttribute(c, 3));
  return g;
}

export const ARCH_PIECES: Piece[] = [

  /* ---------- 창 가리개 (2026-09-15) ----------
   * 조각이 아니라 **창의 속성**이다. RoomDef.dress 를 world.ts emitWindows 가 읽어 창마다 실내 쪽에 굽는다.
   * 원점은 창턱(개구부 바닥) 가운데, 정면(−Z)이 방 안을 본다. 색은 dims.tint(0xRRGGBB) 를 정점색으로. */
  {
    id: 'blind',
    name: '알루미늄 블라인드',
    collection: 'arch',
    category: '건축',
    nofront: true,
    note: '헤드레일 하나에 슬랫 수십 장. open 만큼 위로 걷어 올린 상태(0 = 다 내림). 슬랫은 20° 기울여 빛이 새게.',
    dims: { w: 1.7, h: 1.2, open: 0.3, tint: 0xe6e4de },
    /** 슬랫 다발이 헤드레일 아래에 겹쳐 모인다 — 다 내려간 블라인드를 E 로 걷는 동작.
     *  pivot 을 슬랫 꼭대기(hh = h+0.05)에 두고 gather 로 세로로 오므린다 — 위로 통째로
     *  미는 건 다발이 천장으로 날았다 (2026-09-17, Chris: 「뭔 하늘로 올라가냐」) */
    openable: [{ part: '슬랫', pivot: [0, 1.25, 0], gather: 0.85 }],
    build(w, d) {
      const W = d.w + 0.1;
      const hh = d.h + 0.05;   // 헤드레일은 창머리 위 5cm
      w.part('헤드레일');
      w.add(tinted(new THREE.BoxGeometry(W, 0.045, 0.05).translate(0, hh + 0.02, 0), 0x9a9c9e), 'paper', 0.5);
      const drop = Math.min((d.h + 0.06) * (1 - d.open), d.h + 0.02);   // 내려온 길이. 다 내려도 창턱 3cm 위에서 끝난다 — 턱을 뚫지 않게
      w.movable('슬랫', () => {
      w.part('슬랫');
      const pitch = 0.032, n = Math.max(1, Math.floor(drop / pitch));
      const slats: THREE.BufferGeometry[] = [];
      for (let i = 0; i < n; i++) {
        const g = new THREE.BoxGeometry(W - 0.02, 0.004, 0.03);
        g.rotateX(0.35);
        g.translate(0, hh - i * pitch, 0);
        slats.push(g);
      }
      // 걷어 올린 몫은 헤드레일 밑에 겹쳐 뭉친다
      const bunched = Math.floor(((d.h + 0.06) - drop) / 0.006);
      for (let i = 0; i < bunched; i++) { const g = new THREE.BoxGeometry(W - 0.02, 0.004, 0.03); g.rotateX(0.35); g.translate(0, hh - n * pitch - i * 0.006, 0); slats.push(g); }
      const merged = mergeGeometries(slats, false);
      if (merged) w.add(tinted(merged, d.tint), 'paper', 0.5);
      });
      w.part('줄');
      w.add(tinted(new THREE.BoxGeometry(0.004, drop + 0.3, 0.004).translate(W / 2 - 0.06, hh - (drop + 0.3) / 2, 0.02), 0xd0ccc4), 'paper', 0.3);
    },
  },
  {
    id: 'curtain_pair',
    name: '커튼 두 폭',
    collection: 'arch',
    category: '건축',
    nofront: true,
    note: '봉 하나에 두 폭. open 1 이면 양옆으로 걷혀 창 가장자리만 덮고, 0 이면 가운데서 만난다. 주름은 폭 방향 사인 굴곡.',
    dims: { w: 1.7, h: 1.2, open: 0.7, tint: 0xd8ccb0, panels: 1 },
    /** 폭이 바깥으로 더 걷힌다 — 열려 있는 커튼을 끝까지 젖히는 동작 */
    openable: [
      { part: '왼폭', slide: [-0.3, 0, 0] },
      { part: '오른폭', slide: [0.3, 0, 0] },
    ],
    build(w, d) {
      const top = d.h + 0.14;          // 봉 높이(창턱 기준)
      const len = top + 0.10;          // 창턱 아래 10cm 까지 — **창 길이 커튼**. 바닥까지 늘어뜨리면 창 밑 책상·협탁·침대를 뚫는다(Chris 2026-09-15 컷)
      w.part('봉');
      const rod = new THREE.CylinderGeometry(0.012, 0.012, d.w + 0.6, 10); rod.rotateZ(Math.PI / 2); rod.translate(0, top, 0.02);
      w.add(rod, 'brass', 0.4);
      for (const sx of [-1, 1]) {
        const cap = new THREE.SphereGeometry(0.022, 8, 6); cap.translate(sx * (d.w / 2 + 0.3), top, 0.02); w.add(cap, 'brass', 0.4);
      }
      if (d.panels === 0) return;   // 폭은 천 시뮬레이션 GLB(world.ts emitCurtains)가 건다 — 여기선 봉만
      w.part('폭');
      const panelW = 0.42 + (1 - d.open) * (d.w / 2 - 0.2);
      const seg = Math.max(8, Math.round(panelW / 0.04));
      for (const sx of [-1, 1]) {
        const cx = sx * (d.w / 2 + 0.08 - (1 - d.open) * (d.w / 4));
        w.movable(sx < 0 ? '왼폭' : '오른폭', () => {
        for (const face of [0, 1]) {
          const g = new THREE.PlaneGeometry(panelW, len, seg, 3);
          const pos = g.attributes.position;
          for (let i = 0; i < pos.count; i++) {
            const x = pos.getX(i), y = pos.getY(i);
            const t = (y + len / 2) / len;   // 0 아래 1 위
            pos.setZ(i, Math.sin(x / panelW * Math.PI * 2 * 5) * 0.035 * (0.55 + 0.45 * (1 - t)));   // 아래로 갈수록 주름이 편다
          }
          g.computeVertexNormals();
          if (face) g.rotateY(Math.PI);
          g.translate(cx, top - len / 2 - 0.02, -0.02 + (face ? -0.001 : 0.001));
          w.add(tinted(g, d.tint), 'paper', 0.6);
        }
        });
      }
    },
  },
  {
    id: 'window_slider',
    name: '알루미늄 미서기창',
    collection: 'arch',
    category: '건축',
    note:
      '90 년대 분교에 달린 두 짝 미서기창. 아래 틀에 레일 두 줄이 있고 짝이 그 위를 민다. ' +
      'open 을 주면 오른쪽 짝이 그만큼 밀려 열린다 — 한서진의 집 창은 두 뼘(0.36) 열려 있다.',
    /** 벽에 앉는 창이다 — 창턱이 벽 두께 안으로 내려간다 */
    sunk: true,
    dims: {
      /** 개구부 폭 — 레벨의 half × 2 */
      w: 1.7,
      /** 개구부 높이 — WIN_Y.head - WIN_Y.sill */
      h: 1.2,
      /** 벽에 묻히는 틀 깊이. 벽 두께(0.3)보다 얕아야 양쪽에 벽면이 남는다 */
      depth: 0.11,
      /** 고정틀 부재 폭 */
      frame: 0.05,
      /** 짝틀 부재 폭 */
      sashW: 0.035,
      /** 열린 폭. 0 이면 닫힘 */
      open: 0,
      /** 실내 창턱이 벽에서 내밀는 길이 */
      sill: 0.16,
    },
    /** 오른짝이 왼짝 위로 미끄러져(실내 궤도) 오른쪽이 열린다 — 미서기창의 실제 동작 */
    openable: [{ part: '오른짝', slide: [-0.5, 0, 0] }],
    build(w, d) {
      const hw = d.w / 2;
      const hh = d.h / 2;
      const cy = hh; // 원점이 개구부 바닥이므로 창 중심은 h/2
      const f = d.frame;
      const dep = d.depth;

      // --- 고정틀 (벽 개구부에 물리는 사방 틀) ---
      // 위/아래는 폭 전체를 가로지르고, 좌우는 그 사이를 채운다. 이렇게 짜면
      // 모서리에서 부재가 겹치지 않아 z-파이팅이 나지 않는다.
      w.part('고정틀');
      bar(w, 'alu', d.w, f, dep, 0, d.h - f / 2, 0);          // 상부
      bar(w, 'alu', d.w, f, dep, 0, f / 2, 0);                 // 하부 (레일이 얹힌다)
      bar(w, 'alu', f, d.h - f * 2, dep, -hw + f / 2, cy, 0);  // 좌
      bar(w, 'alu', f, d.h - f * 2, dep, hw - f / 2, cy, 0);   // 우

      // --- 레일 두 줄 ---
      // 미서기창은 짝이 서로 다른 궤도를 탄다. 레일이 한 줄이면 두 짝이 같은 면에
      // 있게 되어 겹치는 구간에서 z-파이팅이 난다. 실물도 두 줄이다.
      w.part('레일');
      const railZ = dep / 2 - 0.022;
      bar(w, 'alu', d.w - f * 2, 0.008, 0.01, 0, f + 0.004, railZ, 0.001);
      bar(w, 'alu', d.w - f * 2, 0.008, 0.01, 0, f + 0.004, railZ - 0.042, 0.001);

      // --- 창짝 두 짝 ---
      // 실외(-Z) 궤도에 왼짝, 실내(+Z) 궤도에 오른짝. 오른짝이 open 만큼 밀린다.
      const paneW = (d.w - f * 2) / 2 + d.sashW;   // 짝은 절반보다 조금 넓다 (맞물림)
      const paneH = d.h - f * 2;
      const sash = (cx: number, cz: number, label: string) => {
        w.part(label);
        const s = d.sashW;
        const phw = paneW / 2;
        const phh = paneH / 2;
        bar(w, 'alu', paneW, s, 0.028, cx, cy + phh - s / 2, cz, 0.0015);
        bar(w, 'alu', paneW, s, 0.028, cx, cy - phh + s / 2, cz, 0.0015);
        bar(w, 'alu', s, paneH - s * 2, 0.028, cx - phw + s / 2, cy, cz, 0.0015);
        bar(w, 'alu', s, paneH - s * 2, 0.028, cx + phw - s / 2, cy, cz, 0.0015);
        // 유리 — 짝틀 안쪽에 0.4mm 물려 넣는다. 면을 맞추면 틀과 같은 평면이 되어
        // 지지직거린다 (docs/MAP_RULES.md 2번).
        w.part(`${label} 유리`);
        const glass = new THREE.BoxGeometry(paneW - s * 2 + 0.004, paneH - s * 2 + 0.004, 0.004);
        glass.translate(cx, cy, cz);
        w.thinSurface(() => w.add(glass, 'pane', 0.9));
      };
      const half = (d.w - f * 2) / 4;
      sash(-half - d.sashW / 2, railZ - 0.042, '왼짝');
      w.movable('오른짝', () => {
      sash(half + d.sashW / 2 + d.open, railZ, '오른짝');

      // --- 손잡이 (오른짝 안쪽 세로대) ---
      // 미서기창 손잡이는 튀어나온 레버가 아니라 세로대에 파 넣은 홈이다.
      w.part('손잡이');
      bar(
        w,
        'alu',
        0.014,
        0.11,
        0.02,
        -half - d.sashW / 2 + paneW / 2 - d.sashW / 2 + d.open + 0.001,
        cy - 0.05,
        railZ + 0.016,
        0.001,
      );
      });

      // --- 실내 창턱 ---
      // 벽에서 내밀어 앉힌 나무 판. 앞코를 조금 두껍게 해야 판이 얇아 보이지 않는다.
      w.part('창턱');
      bar(w, 'oak', d.w + 0.1, 0.035, d.sill, 0, -0.017, dep / 2 + d.sill / 2 - 0.02, 0.004, 1.4);
      bar(w, 'oak', d.w + 0.1, 0.022, 0.024, 0, -0.03, dep / 2 + d.sill - 0.032, 0.003, 1.4);

      // --- 바깥 물받이 ---
      // 비를 벽에서 떼어내는 경사판. 없으면 창 아래 벽에 물이 흐른 자국이 안 읽힌다.
      w.part('물받이');
      const drip = new THREE.BoxGeometry(d.w + 0.06, 0.02, 0.07);
      drip.rotateX(-0.22);
      drip.translate(0, -0.012, -dep / 2 - 0.022);
      w.add(drip, 'alu', 0.9);
    },
  },

  {
    id: 'door_panel',
    name: '목문 문짝',
    collection: 'arch',
    category: '건축',
    note:
      '골조에 판을 끼운 두 쪽 패널 문. 90 년대 시골집 목문이다. ' +
      '**움직이는 부품이라 굽지 않는다** — props.ts 가 힌지 그룹에 얹는다. ' +
      '손잡이 높이가 단서 본문의 숫자와 맞아야 하므로 knobY 를 함부로 바꾸지 말 것.',
    /** 문짝 통째가 한 부품 — 에디터에 놓으면 경첩(-X)을 축으로 앞(-Z)으로 젖힌다.
     * 널판은 `oak`(연한 쪽), 골조는 `walnut` — bakePiece 가 슬롯으로 골조/널판 재질을 가른다 */
    openable: [{ part: '문짝', swing: 1.5, axis: 'y', pivot: [-0.97, 1.09, 0] }],
    dims: {
      w: 1.94,
      h: 2.18,
      /** 문짝 두께 */
      t: 0.08,
      /** 선대(좌우 세로 골조) 폭 */
      stile: 0.14,
      /** 상막이 */
      top: 0.16,
      /** 하막이 — 실물은 아래가 두껍다. 같게 하면 문이 위아래 없이 보인다 */
      bottom: 0.24,
      /** 중간막이 */
      mid: 0.12,
      /** 중간막이 중심 높이 (밑면 기준) */
      midY: 1.02,
      /** 손잡이 중심 높이 */
      knobY: 1.02,
    },
    build(w, d) {
      const hw = d.w / 2;
      const t = d.t;
      const cy = d.h / 2;
      w.movable('문짝', () => {

      // --- 골조 ---
      // 선대가 위아래를 관통하고 막이가 그 사이를 채운다. 실제 문짝 짜임이 그렇고,
      // 이렇게 하면 부재가 모서리에서 겹치지 않는다.
      w.part('선대');
      bar(w, 'walnut', d.stile, d.h, t, -hw + d.stile / 2, cy, 0, 0.003, 1.2);
      bar(w, 'walnut', d.stile, d.h, t, hw - d.stile / 2, cy, 0, 0.003, 1.2);

      const inner = d.w - d.stile * 2;
      w.part('막이');
      bar(w, 'walnut', inner, d.top, t, 0, d.h - d.top / 2, 0, 0.003, 1.2);
      bar(w, 'walnut', inner, d.bottom, t, 0, d.bottom / 2, 0, 0.003, 1.2);
      bar(w, 'walnut', inner, d.mid, t, 0, d.midY, 0, 0.003, 1.2);

      // --- 패널 두 장 ---
      // 골조보다 얇고 양면으로 0.012 들어간다. 면을 맞추면 골조와 같은 평면이 되어
      // 짜임이 안 읽힌다 (docs/MAP_RULES.md 2번).
      // **`oak` 로 둔다** — bakePiece 가 슬롯으로 골조(walnut)/널판(oak)을 갈라 재질을 입힌다
      const panel = (y1: number, y2: number, label: string) => {
        w.part(label);
        const ph = y2 - y1;
        if (ph <= 0.02) return;
        bar(w, 'oak', inner, ph, t - 0.024, 0, (y1 + y2) / 2, 0, 0.002, 1.0);
        // 패널을 둘러싼 턱 — 골조와 패널 사이의 그림자선이 짜임을 읽히게 한다
        const lipT = t - 0.008;
        bar(w, 'walnut', inner, 0.014, lipT, 0, y1 + 0.007, 0, 0.001, 1.0);
        bar(w, 'walnut', inner, 0.014, lipT, 0, y2 - 0.007, 0, 0.001, 1.0);
      };
      panel(d.bottom, d.midY - d.mid / 2, '아래 패널');
      panel(d.midY + d.mid / 2, d.h - d.top, '위 패널');

      // --- 손잡이 ---
      // 원형 노브. 로제트 판을 받쳐야 문짝에 그냥 박힌 구슬로 보이지 않는다.
      // 세그먼트를 낮게 잡는다. 지름 6cm 짜리 구에 14x10 을 주면 손잡이 하나가
      // 문짝 전체 삼각형의 36% 를 먹는다 (문이 13개다). 8x6 이면 이 크기에서 구분이 안 된다.
      w.part('손잡이');
      const knobX = hw - d.stile - 0.05;
      for (const side of [-1, 1]) {
        const rose = new THREE.CylinderGeometry(0.042, 0.042, 0.006, 10);
        rose.rotateX(Math.PI / 2);
        rose.translate(knobX, d.knobY, side * (t / 2 + 0.003));
        w.add(rose, 'brass', 0.9);
        const neck = new THREE.CylinderGeometry(0.016, 0.016, 0.03, 8);
        neck.rotateX(Math.PI / 2);
        neck.translate(knobX, d.knobY, side * (t / 2 + 0.02));
        w.add(neck, 'brass', 0.9);
        const ball = new THREE.SphereGeometry(0.032, 8, 6);
        ball.translate(knobX, d.knobY, side * (t / 2 + 0.05));
        w.add(ball, 'brass', 0.9);
      }

      // --- 경첩 셋 ---
      // 힌지 쪽에 보이게 단다. 없으면 문이 어느 쪽으로 열리는지 형태로 읽히지 않는다.
      w.part('경첩');
      for (const hy of [0.28, d.h / 2, d.h - 0.28]) {
        bar(w, 'steel', 0.05, 0.1, t + 0.008, -hw + 0.025, hy, 0, 0.001, 0.6);
      }
      });
    },
  },

  {
    id: 'door_flush',
    name: '평활 현관문',
    collection: 'arch',
    category: '건축',
    note:
      '2024 년에 지은 조합 주택의 단열 스틸 도어. **목문(`door_panel`)을 여기 쓰면 세대가 한 번 '
      + '밀린다** — 마을은 90 년대 폐교를 고쳐 쓰지만 조합 주택 여섯 채는 신축이고, 그 현관에 '
      + '디지털 도어록(`door_lock`)이 달리는 것이 사건의 뼈대다(03_맵.md §22-4). 짜임이 없는 '
      + '민판에 테두리 띠만 두르고, 현관은 세로 유리 슬릿을 넣는다. `glassW` 를 0 으로 주면 '
      + '실내문이 된다 — 실내문에는 유리 대신 레버만 남는다. '
      + '**상표 형태를 쓰지 않는다**: 특정 제조사의 문양·엠블럼·곡면 커버를 넣지 말 것.',
    dims: {
      w: 1.94,
      h: 2.18,
      /** 문짝 두께 — 단열문이라 목문(0.08)보다 두껍다 */
      t: 0.06,
      /** 테두리 띠 폭. 민판이 통짜 판으로 안 보이게 하는 유일한 선이다 */
      rail: 0.1,
      /** 세로 유리 슬릿 폭. 0 이면 유리 없음(실내문) */
      glassW: 0.18,
      /** 유리 슬릿 높이 */
      glassH: 1.32,
      /** 레버 손잡이 중심 높이. 목문 knobY 와 같게 둔다 — 단서 본문의 숫자다 */
      knobY: 1.02,
    },
    /** 문짝 통째가 한 부품 — 경첩(-X)을 축으로 젖힌다 */
    openable: [{ part: '문짝', swing: 1.5, axis: 'y', pivot: [-0.97, 1.09, 0] }],
    build(w, d) {
      const hw = d.w / 2;
      const t = d.t;
      w.movable('문짝', () => {

      // --- 민판 ---
      // 한 장이다. 조각을 나누면 신축 스틸 도어가 아니라 짜맞춤 목문이 된다.
      //
      // **유리 슬릿이 있으면 민판을 통판으로 두지 마라.** `bar` 는 구멍을 못 뚫는다 —
      // 통판 위에 유리를 겹쳐 놨더니 유리가 판 «속»에 통째로 묻혀서 화면에는 알루미늄
      // 테두리만 남고 안은 문짝 색 그대로였다. 개구부 둘레를 네 조각으로 나눠 «비운다».
      const gx = -hw + d.rail + 0.16 + d.glassW / 2;
      const gy = d.h - d.rail - 0.22 - d.glassH / 2;
      const gl = gx - d.glassW / 2, gr = gx + d.glassW / 2;
      const gb = gy - d.glassH / 2, gt = gy + d.glassH / 2;
      w.part('문짝');
      if (d.glassW > 0.01) {
        bar(w, 'paint', d.w, d.h - gt, t, 0, (gt + d.h) / 2, 0, 0, 1.0);
        bar(w, 'paint', d.w, gb, t, 0, gb / 2, 0, 0, 1.0);
        bar(w, 'paint', gl + hw, gt - gb, t, (-hw + gl) / 2, (gb + gt) / 2, 0, 0, 1.0);
        bar(w, 'paint', hw - gr, gt - gb, t, (gr + hw) / 2, (gb + gt) / 2, 0, 0, 1.0);
      } else {
        bar(w, 'paint', d.w, d.h, t, 0, d.h / 2, 0, 0, 1.0);
      }

      // --- 테두리 띠 ---
      // 민판만 두면 화면에서 **벽에 뚫린 밝은 사각형**으로 읽힌다. 실물 스틸 도어도
      // 가장자리에 단차가 있고, 그 띠 하나가 문을 문으로 만든다.
      //
      // 중심을 겉면에 맞춰 절반을 파묻는다 — 뒷면이 민판 면과 같은 평면이 되는 것을
      // 피한다. **다만 「띠가 안 보인다」는 판정은 화면에서 하지 마라.** 800% 크롭이
      // 4px 짜리 띠를 살짝 비껴갔다는 이유로 「통째로 사라졌다」고 오진한 적이 있다.
      // 조각의 존재 여부는 `node tools/arch-shot.mjs door_flush <base>` 의 슬롯별
      // 삼각형 표로 확인해라 — 그때도 plastic 96 tri 가 멀쩡히 찍혀 있었다.
      w.part('테두리');
      const inset = t / 2;
      const rt = 0.014;
      for (const side of [-1, 1]) {
        bar(w, 'plastic', d.w, d.rail, rt, 0, d.h - d.rail / 2, side * inset, 0, 0.8);
        bar(w, 'plastic', d.w, d.rail, rt, 0, d.rail / 2, side * inset, 0, 0.8);
        bar(w, 'plastic', d.rail, d.h - d.rail * 2, rt, -hw + d.rail / 2, d.h / 2, side * inset, 0, 0.8);
        bar(w, 'plastic', d.rail, d.h - d.rail * 2, rt, hw - d.rail / 2, d.h / 2, side * inset, 0, 0.8);
      }

      // --- 세로 유리 슬릿 ---
      // 경첩 반대쪽(손잡이 쪽)이 아니라 **경첩 쪽**에 둔다. 손잡이 쪽에 두면
      // 도어록 몸체와 겹쳐 둘 다 안 읽힌다.
      if (d.glassW > 0.01) {
        w.part('유리');
        bar(w, 'frost', d.glassW, d.glassH, t - 0.02, gx, gy, 0, 0, 0.6);
        for (const side of [-1, 1]) {
          bar(w, 'alu', d.glassW + 0.03, 0.022, 0.012, gx, gt + 0.011, side * inset, 0, 0.6);
          bar(w, 'alu', d.glassW + 0.03, 0.022, 0.012, gx, gb - 0.011, side * inset, 0, 0.6);
          bar(w, 'alu', 0.022, d.glassH, 0.012, gl - 0.011, gy, side * inset, 0, 0.6);
          bar(w, 'alu', 0.022, d.glassH, 0.012, gr + 0.011, gy, side * inset, 0, 0.6);
        }
      }

      // --- 레버 손잡이 ---
      // 신축 문에 구슬 노브를 달면 목문으로 되돌아간다. 가로로 뻗는 레버다.
      w.part('손잡이');
      const lx = hw - d.rail - 0.09;
      for (const side of [-1, 1]) {
        // 좌판 뒷면이 문짝 겉면과 같은 평면이면 좌판이 사라진다 — 2mm 파묻는다.
        bar(w, 'alu', 0.07, 0.15, 0.012, lx, d.knobY, side * (t / 2 + 0.004), 0, 0.5);
        bar(w, 'alu', 0.026, 0.026, 0.05, lx, d.knobY, side * (t / 2 + 0.036), 0, 0.5);
        bar(w, 'alu', 0.13, 0.024, 0.024, lx - 0.05, d.knobY, side * (t / 2 + 0.05), 0, 0.5);
      }

      // --- 경첩 ---
      // 신축 문은 은장 경첩이고 목문보다 작다.
      w.part('경첩');
      for (const hy of [0.32, d.h / 2, d.h - 0.32]) {
        bar(w, 'alu', 0.04, 0.09, t + 0.006, -hw + 0.018, hy, 0, 0, 0.5);
      }
      });
    },
  },

  {
    id: 'door_school',
    name: '분교 현관 유리문',
    collection: 'arch',
    category: '건축',
    note:
      '백설분교(=조합센터) 현관의 알루미늄 두 짝 여닫이. **한 짝짜리로 두지 마라** — '
      + '개구부가 1.9m 라 외짝으로 채우면 폭 1.9m 짜리 문이 되고, 화면에서는 문이 아니라 '
      + '벽에 붙인 주황색 판때기로 읽힌다. 실제로 그 상태였다. 학교 현관은 두 짝이고, '
      + '두 짝이 만나는 맞선대에 세로 바 손잡이가 하나씩 붙는다. '
      + '유리는 `glass`(투과 0.9 / 불투명도 0.4)라 밤에도 검은 구멍이 되지 않는다 — '
      + '현관동 안쪽 알전구가 그 뒤에서 비친다.',
    dims: {
      w: 1.94,
      h: 2.18,
      t: 0.055,
      /** 세로 프레임(측선대·맞선대) 폭 */
      stile: 0.075,
      /** 상부 가로대 */
      top: 0.09,
      /** 하부 킥플레이트 높이 — 발로 밀고 다니는 자리라 실물도 넓다 */
      kick: 0.34,
      /** 중간 가로대 중심 높이 */
      railY: 1.42,
      /** 중간 가로대 폭 */
      rail: 0.08,
      /** 손잡이 중심 높이 */
      knobY: 1.02,
    },
    /** 두 짝이 바깥 모서리 경첩으로 갈라져 연다 — 왼짝 -X 경첩, 오른짝 +X 경첩 */
    openable: [
      { part: '왼짝', swing: 1.45, axis: 'y', pivot: [-0.97, 1.09, 0] },
      { part: '오른짝', swing: -1.45, axis: 'y', pivot: [0.97, 1.09, 0] },
    ],
    build(w, d) {
      const hw = d.w / 2;
      const t = d.t;

      for (const s of [-1, 1]) {
        w.movable(s < 0 ? '왼짝' : '오른짝', () => {
        // 짝 하나가 0 ~ ±hw 를 차지한다. 가운데가 맞선대다.
        const cx = (s * hw) / 2;
        const iw = hw - d.stile * 2;          // 프레임 안쪽 폭

        w.part(s < 0 ? '왼짝 프레임' : '오른짝 프레임');
        bar(w, 'alu', d.stile, d.h, t, s * (hw - d.stile / 2), d.h / 2, 0, 0, 0.7);
        bar(w, 'alu', d.stile, d.h, t, s * (d.stile / 2), d.h / 2, 0, 0, 0.7);
        bar(w, 'alu', iw, d.top, t, cx, d.h - d.top / 2, 0, 0, 0.7);
        bar(w, 'alu', iw, d.rail, t, cx, d.railY, 0, 0, 0.7);
        // 킥플레이트는 프레임보다 얇게 물려 단차를 남긴다
        bar(w, 'alu', iw, d.kick, t - 0.012, cx, d.kick / 2, 0, 0, 0.7);

        w.part(s < 0 ? '왼짝 유리' : '오른짝 유리');
        const pane = (y1: number, y2: number) => {
          if (y2 - y1 < 0.05) return;
          bar(w, 'glass', iw, y2 - y1, t - 0.024, cx, (y1 + y2) / 2, 0, 0, 0.6);
        };
        pane(d.kick, d.railY - d.rail / 2);
        pane(d.railY + d.rail / 2, d.h - d.top);

        // 맞선대 세로 바 손잡이 — 두 짝이 만나는 쪽에 하나씩, 양면으로.
        w.part(s < 0 ? '왼짝 손잡이' : '오른짝 손잡이');
        const bx = s * (d.stile + 0.03);
        for (const side of [-1, 1]) {
          const z = side * (t / 2 + 0.03);
          bar(w, 'alu', 0.026, 0.56, 0.026, bx, d.knobY, z, 0, 0.5);
          for (const by of [d.knobY - 0.26, d.knobY + 0.26]) {
            bar(w, 'alu', 0.022, 0.022, 0.06, bx, by, side * (t / 2 + 0.015), 0, 0.5);
          }
        }
        });
      }
    },
  },

  {
    id: 'door_shed',
    name: '작업동 합판 양개문',
    collection: 'arch',
    category: '건축',
    note:
      '함석 별동(목공실·도예실·가마실)의 문. 살림집 문도 학교 문도 아니다 — 합판에 각재 '
      + '보강대를 대고 걸쇠를 단 작업장 문이다.\n\n'
      + '**왜 양개(두 짝)인가.** 이 개구부는 1.9m 다. 레벨 주석이 이유를 적어 뒀다 — '
      + '「장작·기물을 밀어 넣는 동선」이라 넓게 뚫은 것이다. 그런데 처음엔 **1.94m 짜리 '
      + '한 짝**으로 만들었고, 그러면 띠경첩 둘에 매달린 폭 2m 합판이 된다. 실물에서 '
      + '띠장문(ledged-and-braced) 한 짝은 1.0~1.1m 가 한계다 — 그보다 넓으면 자기 무게로 '
      + '처지고 경첩이 문설주를 뜯는다. 브레이스가 **장식으로 보였던 진짜 이유가 이것**이다: '
      + '떠받칠 수 없는 문을 떠받치는 시늉을 하고 있었다. 두 짝으로 쪼개면 한 짝이 0.96m 라 '
      + '치수가 맞고, 브레이스 둘이 가운데를 향해 마주 올라가 **구조로 읽힌다.**\n\n'
      + '**브레이스 방향이 어느 쪽으로 열리는지를 말해준다** — 각 짝의 경첩 쪽(바깥 문설주) '
      + '아래에서 손잡이 쪽(가운데 맞댐) 위로 올라간다. 압축을 받아 자유 모서리를 들어 '
      + '올리는 방향이다.\n\n'
      + '**브레이스는 띠장에서 끊는다.** 한 대로 세 띠장을 가로지르면 교차점이 검은 덩어리로 '
      + '뭉쳐 이음매가 아니라 그려 넣은 X 가 된다. 실물도 띠장 사이에 끼워 잘라 넣는다.\n\n'
      + '걸쇠는 잠겨 있지 않고 열려 늘어져 있다 — 작업동은 조합원 전원이 드나든다.',
    dims: {
      w: 1.94,
      h: 2.18,
      /** 합판 두께 */
      t: 0.045,
      /** 각재 보강대 폭 */
      rail: 0.13,
      /** 걸쇠 중심 높이 */
      knobY: 1.02,
      /** 두 짝이 맞대는 틈 */
      gap: 0.012,
    },
    /** 두 짝이 바깥 문설주의 띠경첩으로 바깥(-Z)을 향해 연다. 걸쇠는 오른짝에 붙어 같이 돈다 */
    openable: [
      { part: '왼짝', swing: 1.5, axis: 'y', pivot: [-0.97, 1.09, 0] },
      { part: '오른짝', swing: -1.5, axis: 'y', pivot: [0.97, 1.09, 0] },
    ],
    build(w, d) {
      const hw = d.w / 2;
      const t = d.t;
      /** 한 짝 폭 */
      const lw = (d.w - d.gap) / 2;
      /** 한 짝의 중심 x. s = -1 이 -X 쪽 짝이다 */
      const cx = (lw + d.gap) / 2;
      // 보강대·브레이스는 바깥면(-Z)에만 댄다. 안쪽에도 대면 삼각형만 두 배가 되고
      // 실물도 한 면이다.
      const zf = -(t / 2 + d.rail / 4) + 0.004;
      const rys = [0.26, d.h / 2, d.h - 0.26];
      const hz = zf - d.rail / 4 - 0.006;    // 각재 바깥면 바로 앞

      for (const s of [-1, 1] as const) {
        w.movable(s < 0 ? '왼짝' : '오른짝', () => {
        const xc = s * cx;
        /** 이 짝의 경첩 쪽(바깥) 모서리 */
        const xHinge = s * hw;

        w.part('합판');
        bar(w, 'painted', lw, d.h, t, xc, d.h / 2, 0, 0, 1.0);

        // --- 가로 띠장 ---
        //
        // **띠장에 `ebony` 를 쓰지 마라 — 처마등 밑에서 반짝이 알갱이가 낀다.**
        // ebony 는 darkwood 절차 노멀에 roughness 0.42 다. 문 바로 위 처마등이 스치는
        // 각으로 때리면 그 고주파 노멀이 램프 색(따뜻한 황갈색) 스페큘러로 튀어, 위쪽
        // 띠장과 브레이스 «윗동강»만 모래알처럼 얼룩졌다. 아래 띠장은 멀쩡했다 —
        // 밝기·입사각에 따라 달라지는 게 단서다. z-파이팅이면 조명과 무관해야 한다.
        // (처음에 z-파이팅으로 오진해 4mm 파묻었다. 그건 그대로 둬도 해가 없어 남긴다.)
        // `coal`(#2b2724, r 0.99, 노멀맵 없음)로 바꾸니 한 번에 사라졌다.
        w.part('띠장');
        for (const ry of rys) bar(w, 'coal', lw - 0.03, d.rail, d.rail / 2, xc, ry, zf, 0, 0.9);

        // --- 대각 브레이스 — 띠장 칸마다 한 도막씩 ---
        // `bar` 는 축정렬만 되므로 여기만 직접 돌린다.
        w.part('브레이스');
        for (let i = 0; i < rys.length - 1; i++) {
          const dy = rys[i + 1] - rys[i];
          const dx = lw - 0.09;
          const g = new THREE.BoxGeometry(Math.hypot(dx, dy), d.rail * 0.8, d.rail / 2);
          // 바깥(경첩) 아래 → 가운데(손잡이) 위. -X 짝은 +기울기, +X 짝은 -기울기다
          g.rotateZ(-s * Math.atan2(dy, dx));
          // 띠장과 같은 z 에 두면 교차 지점에서 앞면끼리 겹쳐 지지직거린다. 3mm 뒤로.
          g.translate(xc, (rys[i] + rys[i + 1]) / 2, zf + 0.003);
          w.add(g, 'coal', 0.9);
        }

        // --- 띠경첩 ---
        // 작업동답게 **긴 띠경첩**이다. 0.2m 짜리는 문틀 문선에 물려 보이지도 않았다 —
        // 띠장까지 닿는 길이여야 문 성격이 실루엣에 남는다.
        //
        // **철물은 띠장 «위»에 얹는다.** 합판 면(z=-t/2)에 붙였더니 걸쇠도 경첩도
        // 각재(면이 0.0875 나와 있다) 속에 통째로 파묻혔다. 실물도 띠경첩은 띠장 위에 친다.
        //
        // **`steel` 을 쓰지 마라.** 이 씬은 `scene.environment` 를 일부러 안 걸어서
        // metalness 가 0.1 을 넘는 슬롯은 반사할 하늘이 없다 — 새까맣게 떨어진다.
        // 그렇다고 `plastic`(#2c2e33)도 안 된다: 띠장이 `coal`(#2b2724)이라 **경첩이
        // 띠장에 먹혀 화면에서 사라진다.** 컷을 열어 보니 문 한쪽에 걸쇠만 있고 반대쪽에
        // 아무것도 없어서, 브레이스가 가리키는 「열리는 쪽」을 읽을 수가 없었다.
        // 아연도금 띠경첩으로 보고 `alu`(m .58, 밝은 회색)로 낸다 — 걸쇠와 같은 금속이다.
        w.part('경첩');
        for (const hy of [rys[0], rys[2]]) {
          bar(w, 'alu', 0.40, 0.075, 0.01, xHinge - s * 0.22, hy, hz, 0, 0.6);
          bar(w, 'alu', 0.07, 0.11, t + 0.008, xHinge - s * 0.03, hy, 0, 0, 0.6);
        }

        // --- 손잡이 ---
        // 맞댐 쪽에 짝마다 하나씩. 양개문은 여기를 잡아 당긴다
        w.part('손잡이');
        bar(w, 'alu', 0.05, 0.05, 0.06, xc - s * (lw / 2 - 0.13), d.knobY, hz - 0.035, 0, 0.5);

        // --- 걸쇠 ---
        // 두 짝을 가로질러 거는 판이다. 한 짝짜리 문의 「문설주 고리」와 달리 양개문은
        // **맞댐을 물어야** 닫힌 것으로 읽힌다. 늘어져 있으므로 한쪽에만 걸려 있다.
        // 오른짝에 붙은 물건이라 그 짝이 열리면 같이 돌아간다 — 정적 부품이면 문이 열릴 때
        // 허공에 뜬 걸쇠가 된다
        if (s > 0) {
          w.part('걸쇠');
          bar(w, 'alu', 0.30, 0.075, 0.01, 0.02, d.knobY, hz, 0, 0.5);
        }
        });
      }
    },
  },

  {
    id: 'door_casing',
    name: '문틀과 문선',
    collection: 'arch',
    category: '건축',
    note:
      '벽 개구부에 물리는 문틀. 문선(casing)이 벽면 양쪽으로 덮고, 안쪽에 문이 닿는 ' +
      '멈춤턱이 있다. 정적이라 StaticBatcher 에 굽는다.',
    dims: {
      /** 개구부 폭 — DOORWAYS 의 half × 2 */
      w: 1.9,
      /** 개구부 높이 */
      h: 2.2,
      /** 벽 두께 */
      wall: 0.3,
      /** 문선 폭 (벽면을 덮는 띠) */
      casing: 0.11,
      /** 문선이 벽면에서 튀어나오는 양 (눈에 보이는 두께) */
      relief: 0.02,
      /**
       * 문선 부재의 실제 두께. **relief 보다 훨씬 두꺼워야 한다.**
       * 그림자 라이트의 normalBias 가 0.025 인데(world.ts:511) 부재가 그보다 얇으면
       * 그림자 샘플이 판을 관통해 반대편으로 넘어가 표면이 흰 점으로 얼룩진다.
       * 처음에 0.024 로 만들었더니 문틀 주변이 통째로 지지직거렸다 — z-파이팅이 아니라
       * 그림자 acne 였다. 나머지는 벽 속에 묻히므로 보이지 않는다.
       */
      casingT: 0.07,
      /** 문틀 부재 폭 (개구부를 좁히는 안턱) */
      jamb: 0.05,
      /**
       * 멈춤턱. **normalBias(0.025)보다 두꺼워야 한다.**
       * 16mm 로 만들었더니 그림자 샘플이 각재를 관통해 문틀 안쪽 테두리가 흰 점으로
       * 얼룩졌다. 실물은 15mm 쯤이지만 그림자가 깨지는 것보다는 두꺼운 게 낫다.
       */
      stop: 0.034,
    },
    build(w, d) {
      const hw = d.w / 2;
      const j = d.jamb;
      const wall = d.wall;

      // --- 문틀 (개구부 안쪽을 두르는 부재) ---
      w.part('문틀');
      bar(w, 'trim', j, d.h - j, wall, -hw + j / 2, (d.h - j) / 2, 0, 0.003, 1.2);
      bar(w, 'trim', j, d.h - j, wall, hw - j / 2, (d.h - j) / 2, 0, 0.003, 1.2);
      bar(w, 'trim', d.w, j, wall, 0, d.h - j / 2, 0, 0.003, 1.2);

      // --- 멈춤턱 ---
      // 문이 닫힐 때 닿는 턱. 문틀 깊이의 한가운데가 아니라 실내 쪽으로 치우쳐 있다.
      w.part('멈춤턱');
      const stopZ = wall / 2 - 0.09;
      bar(w, 'trim', d.stop, d.h - j, d.stop, -hw + j + d.stop / 2, (d.h - j) / 2, stopZ, 0.001, 0.8);
      bar(w, 'trim', d.stop, d.h - j, d.stop, hw - j - d.stop / 2, (d.h - j) / 2, stopZ, 0.001, 0.8);
      bar(w, 'trim', d.w - j * 2, d.stop, d.stop, 0, d.h - j - d.stop / 2, stopZ, 0.001, 0.8);

      // --- 문선 (양쪽 벽면을 덮는 띠) ---
      // 벽과 문틀의 이음을 가리는 부재다. 이게 없으면 벽에 낸 구멍처럼 보인다.
      w.part('문선');
      for (const side of [-1, 1]) {
        // **벽 안으로 물려 넣는다.** 문선 두께의 절반만 밖에 두면 뒷면이 벽면과 정확히
        // 같은 평면이 되어 지지직거린다 (내가 처음 그렇게 짰고 문틀 주변이 얼룩졌다).
        // 뒷면을 벽 속에 묻으면 경쟁할 상대가 없다 — docs/MAP_RULES.md 2번.
        const z = side * (wall / 2 - d.casingT / 2 + d.relief);
        // 세로 두 짝이 위아래를 관통하고 가로가 그 사이를 채운다
        bar(w, 'trim', d.casing, d.h + d.casing, d.casingT, -hw - d.casing / 2, (d.h + d.casing) / 2, z, 0.002, 1.0);
        bar(w, 'trim', d.casing, d.h + d.casing, d.casingT, hw + d.casing / 2, (d.h + d.casing) / 2, z, 0.002, 1.0);
        bar(w, 'trim', d.w, d.casing, d.casingT, 0, d.h + d.casing / 2, z, 0.002, 1.0);
      }
    },
  },

  {
    id: 'wall_trim',
    name: '벽 몰딩 한 벌',
    collection: 'arch',
    category: '건축',
    note:
      '걸레받이 · 허리몰딩 · 크라운을 한 벌로 만든다. 벽 세그먼트 하나에 하나씩 굽는다. ' +
      '**벽 본체는 조각이 아니다** — 회벽은 worldUV 로 씌워야 인접한 벽끼리 결이 이어진다. ' +
      '조각의 localUV 로 바꾸면 203 세그먼트마다 텍스처가 리셋돼 벽마다 이음선이 드러난다.',
    /** 걸레받이·허리몰딩·크라운이 **벽의 다른 높이에** 붙고 벽 양면에 각각 달린다 */
    clusters: 6,
    dims: {
      /** 벽 세그먼트 길이 */
      len: 4,
      /** 벽 높이 */
      h: 3.2,
      /** 벽 두께 */
      wall: 0.3,
      /** 걸레받이 높이 */
      skirtH: 0.19,
      /** 걸레받이가 벽에서 나오는 두께 */
      skirtT: 0.04,
      /** 허리몰딩 중심 높이. 0 이면 달지 않는다 */
      chairY: 1.02,
      /** 크라운 중심 높이. 0 이면 달지 않는다 */
      crownY: 3.11,
      /**
       * 몰딩을 달 면. `0` 이면 양면, `-1`/`+1` 이면 그 쪽 한 면만.
       *
       * **외벽에는 반드시 실내 쪽만 달아라.** 허리몰딩은 벽면에서 3.8cm 나오는데
       * 외벽 마감(함석 3.4cm)보다 4mm 더 나온다. 그 4mm 짜리 흰 띠가 32m 벽을
       * 가로지르면 화면에서 **1px 검은 실선** 하나로 떨어진다 — 벽 전체를 자르는
       * 그 선이 「결이 이상하다」의 정체였다.
       */
      sides: 0,
    },
    build(w, d) {
      const half = d.wall / 2;

      // 벽 세그먼트는 어느 쪽이 실내인지 스스로 모른다. 아는 쪽(world.ts 가 건물
      // 외곽선과 대조한다)이 `sides` 로 알려주면 그 면만, 아니면 양면에 단다.
      for (const side of d.sides ? [d.sides] : [-1, 1]) {
        // --- 걸레받이 ---
        // 3단으로 짠다. 한 장 박스로 두면 벽에 띠를 붙인 것처럼 보이고, 계단이 져야
        // 빛이 단마다 다르게 앉아 부재로 읽힌다. 아래가 두껍고 위로 얇아진다.
        if (d.skirtH > 0) {
        w.part('걸레받이');
        const s1 = d.skirtT;
        const s2 = d.skirtT * 0.72;
        const s3 = d.skirtT * 0.42;
        const b1 = d.skirtH * 0.62;
        const b2 = d.skirtH * 0.26;
        const b3 = d.skirtH - b1 - b2;
        bar(w, 'trim', d.len, b1, s1, 0, b1 / 2, side * (half + s1 / 2), 0, 1.1);
        bar(w, 'trim', d.len, b2, s2, 0, b1 + b2 / 2, side * (half + s2 / 2), 0, 1.1);
        bar(w, 'trim', d.len, b3, s3, 0, b1 + b2 + b3 / 2, side * (half + s3 / 2), 0, 1.1);
        }

        // --- 허리몰딩 ---
        // 벽이 평면이 아니라 벽으로 읽히게 하는 수평선. 2단이면 충분하다.
        if (d.chairY > 0) {
          w.part('허리몰딩');
          bar(w, 'trim', d.len, 0.045, 0.026, 0, d.chairY, side * (half + 0.013), 0, 1.1);
          bar(w, 'trim', d.len, 0.018, 0.038, 0, d.chairY - 0.028, side * (half + 0.019), 0, 1.1);
        }

        // --- 크라운 ---
        // 천장과 벽이 만나는 자리. 여기가 비면 방이 상자처럼 보인다.
        if (d.crownY > 0) {
          w.part('크라운');
          bar(w, 'trim', d.len, 0.055, 0.03, 0, d.crownY, side * (half + 0.015), 0, 1.1);
          bar(w, 'trim', d.len, 0.03, 0.052, 0, d.crownY - 0.04, side * (half + 0.026), 0, 1.1);
        }
      }
    },
  },
];
